import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@urlshot/ui';
import {
  billingStateOf,
  queryKeys,
  useBillingOverview,
  useChangePlan,
  usePlanChangePreview,
  usePortalSession,
  useSession,
  useStartCheckout,
  useUsageSummary,
  type BillingInterval,
  type BillingOverview,
  type PlanChangePreview,
} from '../../lib/api';
import { env, isBillingConfigured } from '../../env';
import { openCheckout } from '../../lib/paddle';
import { CreditMeter } from '../../components/credit-meter';
import { Button, Card, FormError, SegmentedControl } from '../../components/ui';

/**
 * Billing.
 *
 * The page never decides that a purchase happened. Checkout and plan changes are requests to
 * Paddle; the plan shown here moves only when the Platform API reports it moved, which it does only
 * after Paddle's signed webhook arrives. So after a checkout the page says it is confirming and
 * polls, rather than showing the new plan the moment the overlay closes.
 *
 * Without Paddle configured the screen says so plainly instead of offering controls that cannot
 * work -- the specification requires the application to run locally without credentials.
 */

type Price = BillingOverview['prices'][number];
type Plan = BillingOverview['plans'][number];

/** Statuses under which the workspace is on its paid plan: the same set the API uses. */
const ENTITLING = new Set(['active', 'trialing', 'past_due']);

const FREE_PLAN = 'free';

/**
 * The plan the page recommends: its card is outlined and its button is the one filled button.
 *
 * Kept in step with `featured` in `apps/site/src/lib/pricing.ts`, so the dashboard and
 * urlshot.io/pricing point a buyer at the same plan.
 */
const FEATURED_PLAN = 'growth';

/** After this long, say that confirmation is slow rather than leave a spinner running silently. */
const SLOW_AFTER_MS = 60_000;

function formatMoney(amountCents: number, currency: string, alwaysCents = false): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: alwaysCents || amountCents % 100 !== 0 ? 2 : 0,
  }).format(amountCents / 100);
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

/** A limit in seconds, as the pricing page writes it: "15 s", "5 min", "24 hours". */
function duration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${String(totalSeconds)} s`;
  if (totalSeconds < 3600) return `${String(totalSeconds / 60)} min`;
  const hours = totalSeconds / 3600;
  return hours === 1 ? '1 hour' : `${String(hours)} hours`;
}

/** A page on the marketing site. Absolute, because the dashboard is served from another origin. */
function siteUrl(path: string): string {
  return new URL(path, env.PUBLIC_SITE_URL).href;
}

/**
 * Months of the monthly price that paying yearly saves, or null when it saves none.
 *
 * Months rather than a percentage, because that is how the prices are set -- a year costs ten
 * months -- and "2 months free" is checkable at a glance where "17% off" is not.
 */
function monthsFreeOf(yearly: Price, prices: readonly Price[]): number | null {
  const monthly = prices.find(
    (price) =>
      price.planCode === yearly.planCode &&
      price.interval === 'month' &&
      price.currency === yearly.currency,
  );
  if (monthly === undefined || monthly.amountCents === 0) return null;
  const months = Math.round(12 - yearly.amountCents / monthly.amountCents);
  return months > 0 ? months : null;
}

/** What a yearly price works out to per month, and what it saves against paying monthly. */
function yearlyComparison(yearly: Price, prices: readonly Price[]): string {
  const perMonth = formatMoney(Math.round(yearly.amountCents / 12), yearly.currency, true);
  const monthsFree = monthsFreeOf(yearly, prices);
  return monthsFree !== null
    ? `${perMonth} a month, billed yearly · ${String(monthsFree)} months free`
    : `${perMonth} a month, billed yearly`;
}

/** The saving, when every plan sold yearly saves the same -- so the switch can say it once. */
function uniformMonthsFree(prices: readonly Price[]): number | null {
  const saved = prices
    .filter((price) => price.interval === 'year')
    .map((price) => monthsFreeOf(price, prices));
  const first = saved[0];
  return first !== undefined && first !== null && saved.every((months) => months === first)
    ? first
    : null;
}

/**
 * The monthly price divided by the screenshots it includes: the comparison a buyer actually makes.
 *
 * Up to four decimals, as on the pricing page: the rates span an order of magnitude, and a fixed
 * precision that suits the entry plan rounds the largest one into a different number.
 */
function pricePerScreenshot(price: Price): string | null {
  if (price.interval !== 'month' || price.monthlyCredits === null) return null;
  if (price.monthlyCredits <= 0 || price.amountCents <= 0) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(price.amountCents / 100 / price.monthlyCredits);
}

function formatDate(value: string | null): string {
  if (value === null) return 'the end of the period';
  return new Date(value).toLocaleDateString('en-US', { dateStyle: 'long' });
}

interface Awaiting {
  /**
   * The plan and billing interval the workspace was on when the request was made, as
   * `billingStateOf` spells them. Waiting ends when either changes: a switch to yearly on the same
   * plan changes only the interval.
   */
  from: string;
  slow: boolean;
}

export function BillingRoute() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [awaiting, setAwaiting] = useState<Awaiting | null>(null);
  const { data: billing, isLoading } = useBillingOverview(awaiting?.from ?? null);
  const [error, setError] = useState<string | null>(null);

  // Waiting ends when the reported state is no longer the one the request was made from: the
  // webhook has landed. Derived rather than stored, so it cannot disagree with the data.
  const waiting =
    awaiting !== null && (billing === undefined || billingStateOf(billing) === awaiting.from);
  const billingState = billing === undefined ? undefined : billingStateOf(billing);
  const previousBillingState = useRef(billingState);

  // Everything else that shows the plan -- the overview, the header -- is stale once it moves, and a
  // switch to yearly also starts a fresh month of credits. Only a move counts: the first load is not
  // one, and refetching them on every visit to this page would be noise.
  useEffect(() => {
    const previous = previousBillingState.current;
    previousBillingState.current = billingState;
    if (previous === undefined || billingState === undefined || previous === billingState) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.workspace });
    void queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }, [billingState, queryClient]);

  useEffect(() => {
    if (awaiting === null || awaiting.slow) return;
    const timer = setTimeout(() => {
      setAwaiting((current) => (current === null ? null : { ...current, slow: true }));
    }, SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [awaiting]);

  const enabled = isBillingConfigured && billing?.billingEnabled === true;

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">Billing</h1>
        <p className="mt-1 text-sm text-neutral-600">Your plan, and what it costs.</p>
      </div>

      {waiting && (
        <Card className="border-brand-400 ring-1 ring-brand-400" data-testid="billing-confirming">
          <h2 className="text-base font-semibold">Confirming with Paddle…</h2>
          <p className="mt-1 text-sm text-neutral-600">
            {awaiting.slow
              ? 'This is taking longer than usual. Your payment is safe; the plan will update here as soon as Paddle confirms it, and you can leave this page.'
              : 'Your new plan appears here as soon as Paddle confirms it, usually within a few seconds.'}
          </p>
        </Card>
      )}

      <FormError message={error} />

      <CurrentPlan billing={billing} isLoading={isLoading} enabled={enabled} onError={setError} />

      {enabled && billing !== undefined ? (
        <Plans
          billing={billing}
          email={session?.user.email}
          busy={waiting}
          onError={setError}
          onRequested={() => {
            setError(null);
            setAwaiting({ from: billingStateOf(billing), slow: false });
          }}
        />
      ) : (
        billing !== undefined && (
          <Card>
            <h2 className="text-base font-semibold">Upgrade</h2>
            <p className="mt-2 text-sm text-neutral-600" data-testid="billing-disabled">
              Billing is not configured in this environment, so upgrades and invoices are
              unavailable. The Free plan remains fully usable.
            </p>
          </Card>
        )
      )}
    </>
  );
}

function CurrentPlan({
  billing,
  isLoading,
  enabled,
  onError,
}: {
  billing: BillingOverview | undefined;
  isLoading: boolean;
  enabled: boolean;
  onError: (message: string | null) => void;
}) {
  const portal = usePortalSession();
  // The billing period's credits, which do not depend on the window asked for.
  const { data: usage } = useUsageSummary();
  const subscription = billing?.subscription ?? null;
  const pastDue = subscription?.status === 'past_due';

  const openPortal = async () => {
    onError(null);
    try {
      const { url } = await portal.mutateAsync();
      window.location.assign(url);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'The billing portal could not be opened.');
    }
  };

  return (
    <Card className={pastDue ? 'border-red-300' : ''} data-testid="billing-current">
      <h2 className="text-xs uppercase tracking-wide text-neutral-500">Current plan</h2>
      <p className="mt-1 text-lg font-semibold">{isLoading ? '—' : (billing?.plan.name ?? '—')}</p>
      <p className="mt-1 text-sm text-neutral-600" data-testid="billing-status">
        {statusLine(billing)}
      </p>

      {usage !== undefined && (
        <div className="mt-4">
          <CreditMeter
            used={usage.creditsUsedThisPeriod}
            included={usage.monthlyCredits}
            resetsAt={usage.billingPeriod.endsAt}
            data-testid="billing-credit-meter"
          />
        </div>
      )}

      {enabled && subscription !== null && (
        <div className="mt-4">
          <Button variant="secondary" onClick={() => void openPortal()} disabled={portal.isPending}>
            {pastDue ? 'Update payment method' : 'Manage billing and invoices'}
          </Button>
        </div>
      )}
    </Card>
  );
}

function statusLine(billing: BillingOverview | undefined): string {
  if (billing === undefined) return '';
  const subscription = billing.subscription;

  if (subscription?.status === 'canceled' || subscription === null) {
    return billing.plan.code === FREE_PLAN
      ? 'Free plan. Upgrade for more credits and concurrent renders.'
      : '';
  }

  switch (subscription.status) {
    case 'active':
      return subscription.cancelAtPeriodEnd
        ? `Cancels on ${formatDate(subscription.currentPeriodEnd)}. You keep ${billing.plan.name} until then.`
        : `Billed ${subscription.interval === 'year' ? 'yearly' : 'monthly'}. Renews on ${formatDate(subscription.currentPeriodEnd)}.`;
    case 'past_due':
      return 'Your last payment did not go through. Paddle is retrying the card; update your payment method to keep this plan.';
    case 'paused':
      return 'Your subscription is paused. The workspace is on the Free plan until it resumes.';
    case 'trialing':
      return `Trial ends on ${formatDate(subscription.currentPeriodEnd)}.`;
  }
}

/** What a subscriber is told before a change is sent to Paddle. */
function changeNotice(toYearly: boolean): string {
  return toYearly
    ? 'Paddle charges the yearly price now, less the unused part of this month. Your renewal date moves to today, and a fresh month of credits starts.'
    : 'Paddle charges or credits the difference now.';
}

/** An amount in a currency's minor units, formatted: 8100 USD reads "$81.00". */
function formatMinor(amount: number, currency: string): string {
  const format = new Intl.NumberFormat('en-US', { style: 'currency', currency });
  // The currency's own number of decimals: two for USD and EUR, none for JPY.
  const fraction = format.formatToParts(1).find((part) => part.type === 'fraction');
  const digits = fraction === undefined ? 0 : fraction.value.length;
  return format.format(amount / 10 ** digits);
}

/**
 * What confirming a plan change will do to the customer's card, in Paddle's own numbers.
 *
 * Asked for when the confirm step opens, with the same request the change sends. While it is being
 * calculated, or if Paddle cannot calculate it, the general notice stands in: the change is still
 * safe to make, and Paddle's receipt shows the charge.
 */
function ChangePreview({
  state,
  toYearly,
}: {
  state: { data: PlanChangePreview | undefined; isPending: boolean };
  toYearly: boolean;
}) {
  if (state.isPending) {
    return <p className="text-sm text-neutral-600">Calculating the charge…</p>;
  }
  const preview = state.data;
  if (preview === undefined) {
    return <p className="text-sm text-neutral-600">{changeNotice(toYearly)}</p>;
  }

  const money = (amount: number) => formatMinor(amount, preview.currency);
  const lines: string[] = [];
  if (preview.chargeNow > 0)
    lines.push(`${money(preview.chargeNow)} is charged now, tax included.`);
  if (preview.credit > 0) {
    lines.push(
      `${money(preview.credit)} is credited to your Paddle balance and used on your next invoices.`,
    );
  }
  if (preview.chargeNow <= 0 && preview.credit <= 0) lines.push('Nothing is charged now.');
  if (preview.recurring !== null && preview.nextBilledAt !== null) {
    lines.push(
      `Then ${money(preview.recurring)} a ${preview.interval}, next on ${formatDate(preview.nextBilledAt)}.`,
    );
  }
  if (toYearly)
    lines.push('Your renewal date moves to today, and a fresh month of credits starts.');

  return (
    <div className="space-y-1 text-sm text-neutral-600" data-testid="billing-change-preview">
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-sm text-neutral-500">{children}</p>;
}

function Plans({
  billing,
  email,
  busy,
  onError,
  onRequested,
}: {
  billing: BillingOverview;
  email: string | undefined;
  busy: boolean;
  onError: (message: string | null) => void;
  onRequested: () => void;
}) {
  const checkout = useStartCheckout();
  const changePlan = useChangePlan();
  const preview = usePlanChangePreview();
  const [confirming, setConfirming] = useState<string | null>(null);

  const subscription = billing.subscription;
  const entitled = subscription !== null && ENTITLING.has(subscription.status);
  const hasYearly = billing.prices.some((price) => price.interval === 'year');
  const [interval, setInterval] = useState<BillingInterval>(
    entitled ? subscription.interval : 'month',
  );
  const prices = billing.prices.filter((price) => price.interval === interval);
  const savedMonths = uniformMonthsFree(billing.prices);

  // Yearly subscribers see yearly prices only: switching back to monthly is not offered here, and
  // the API refuses it (see `BillingService.changePlan`). Everyone else may look at both.
  const showIntervalSwitch = hasYearly && (!entitled || subscription.interval === 'month');

  const upgrade = async (planCode: string) => {
    onError(null);
    try {
      const { transactionId } = await checkout.mutateAsync({ planCode, interval });
      await openCheckout({ transactionId, email, completed: onRequested });
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Checkout could not be started.');
    }
  };

  const switchTo = async (planCode: string, to: BillingInterval) => {
    onError(null);
    setConfirming(null);
    try {
      await changePlan.mutateAsync({ planCode, interval: to });
      onRequested();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'The plan could not be changed.');
    }
  };

  /** Open the confirm step for a change, and ask Paddle what that change would charge. */
  const confirmChange = (key: string, planCode: string, to: BillingInterval) => {
    onError(null);
    setConfirming(key);
    preview.reset();
    preview.mutate({ planCode, interval: to });
  };

  const pending = busy || checkout.isPending || changePlan.isPending;

  /** What a card offers: at most one button, outlined unless the card is the featured plan. */
  const actionFor = (plan: Plan, price: Price | undefined): ReactNode => {
    if (plan.code === FREE_PLAN) {
      if (billing.plan.code === FREE_PLAN) return <Muted>Current plan</Muted>;
      return entitled ? <Muted>Cancel from Manage billing to return to Free</Muted> : null;
    }
    if (price === undefined) return null;

    const key = `${price.planCode}-${price.interval}`;
    const onThisPlan = billing.plan.code === price.planCode;
    // A subscriber's current plan is a plan *and* an interval: Startup yearly is not the plan a
    // monthly Startup subscriber is on, it is the one they can switch to.
    const current = onThisPlan && (!entitled || price.interval === subscription.interval);
    const toYearly = entitled && subscription.interval === 'month' && price.interval === 'year';
    const variant = plan.code === FEATURED_PLAN ? 'primary' : 'secondary';

    if (current) return <Muted>Current plan</Muted>;
    if (!price.purchasable) return <Muted>Not available yet</Muted>;

    if (!entitled) {
      return (
        <Button
          variant={variant}
          className="w-full"
          onClick={() => void upgrade(price.planCode)}
          disabled={pending}
        >
          Upgrade to {price.planName}
        </Button>
      );
    }
    if (subscription.status !== 'active') return <Muted>Available once payment is settled</Muted>;
    if (subscription.cancelAtPeriodEnd) {
      // Paddle refuses any change while a cancellation is scheduled, and so does the API.
      return <Muted>Not available while cancellation is scheduled</Muted>;
    }

    if (confirming === key) {
      return (
        <div className="flex flex-col gap-2">
          <ChangePreview state={preview} toYearly={toYearly} />
          <Button
            variant="primary"
            onClick={() => void switchTo(price.planCode, price.interval)}
            // Not while the amount is still being calculated: the customer confirms what they read.
            disabled={pending || preview.isPending}
          >
            Confirm
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
        </div>
      );
    }

    const actionLabel = toYearly
      ? onThisPlan
        ? 'Switch to yearly'
        : `Switch to ${price.planName}, yearly`
      : `Switch to ${price.planName}`;
    return (
      <Button
        variant={variant}
        className="w-full"
        onClick={() => confirmChange(key, price.planCode, price.interval)}
        disabled={pending}
      >
        {actionLabel}
      </Button>
    );
  };

  return (
    <Card data-testid="billing-plans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold">{entitled ? 'Change plan' : 'Upgrade'}</h2>
        {showIntervalSwitch && (
          <SegmentedControl
            label="Billing interval"
            options={[
              { value: 'month', label: 'Monthly' },
              {
                value: 'year',
                label:
                  savedMonths === null ? 'Yearly' : `Yearly · ${String(savedMonths)} months free`,
              },
            ]}
            value={interval}
            onChange={(option) => {
              setInterval(option);
              setConfirming(null);
            }}
          />
        )}
      </div>

      <ul className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {billing.plans.map((plan) => {
          const featured = plan.code === FEATURED_PLAN;
          const price = prices.find((candidate) => candidate.planCode === plan.code);
          const each = price === undefined ? null : pricePerScreenshot(price);

          return (
            <li
              key={plan.code}
              className={cn(
                'flex flex-col rounded-lg border p-4',
                featured ? 'border-brand-400 ring-1 ring-brand-400' : 'border-neutral-200',
              )}
              data-testid={`billing-plan-${plan.code}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold">{plan.name}</h3>
                {featured && (
                  <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-800">
                    Most popular
                  </span>
                )}
              </div>

              <div className="mt-3 min-h-16">
                {plan.code === FREE_PLAN ? (
                  <>
                    <p className="text-2xl font-semibold tracking-tight">Free</p>
                    <p className="text-xs text-neutral-500">No card needed</p>
                  </>
                ) : price === undefined ? (
                  <p className="text-sm text-neutral-500">
                    Not available {interval === 'year' ? 'yearly' : 'monthly'}
                  </p>
                ) : (
                  <>
                    <p className="text-2xl font-semibold tracking-tight">
                      {formatMoney(price.amountCents, price.currency)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      / {price.interval === 'month' ? 'month' : 'year'} excl. tax
                    </p>
                    {price.interval === 'year' ? (
                      <p
                        className="mt-1 text-xs text-neutral-500"
                        data-testid={`billing-yearly-${plan.code}`}
                      >
                        {yearlyComparison(price, billing.prices)}
                      </p>
                    ) : (
                      each !== null && (
                        <p
                          className="mt-1 text-xs text-neutral-500"
                          data-testid={`billing-each-${plan.code}`}
                        >
                          {each} each
                        </p>
                      )
                    )}
                  </>
                )}
              </div>

              <ul className="mt-4 flex-1 space-y-1.5 text-sm text-neutral-700">
                <li>{formatCount(plan.limits.monthlyCredits)} screenshots a month</li>
                <li>
                  {plan.limits.concurrentRenders} concurrent{' '}
                  {plan.limits.concurrentRenders === 1 ? 'render' : 'renders'}
                </li>
                <li>Up to {duration(plan.limits.maxTimeoutMs / 1000)} per render</li>
                <li>Cache up to {duration(plan.limits.maxCacheTtl)}</li>
              </ul>

              <div className="mt-4">{actionFor(plan, price)}</div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 space-y-2 text-xs text-neutral-500">
        <p data-testid="billing-tax-note">
          Prices exclude VAT and sales tax. Paddle adds them at checkout where your country requires
          them, and shows the total before you pay.
        </p>
        <p data-testid="billing-refund-note">
          Cancel any time and keep your plan until the end of the period you paid for. Every payment
          is refundable within 14 days under the{' '}
          <a href={siteUrl('/refunds/')} className="link">
            refund policy
          </a>
          .
        </p>
        {entitled && subscription.interval === 'year' && (
          <p data-testid="billing-yearly-note">
            You are billed yearly. To move to monthly billing, contact support.
          </p>
        )}
        <p>
          <a href={siteUrl('/pricing/')} className="link">
            Compare plans in detail on urlshot.io
          </a>
        </p>
      </div>
    </Card>
  );
}
